import React, { useMemo, useState } from "react";
import { SettingsFormContent } from "@/components/pages/Settings";
import { useGetCountries } from "@/services/RegisterCompany";
import { ColumnDef } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { AddressType, CreateAddress } from "@/types";
import Space from "@/components/atoms/Space";
import Text from "@/components/atoms/Text";
import CTA from "@/components/atoms/CTA";
import Group from "@/components/atoms/Group";
import SettingsTable from "@/components/molecules/SettingsTable/SettingsTable";
import { useAuth } from "@/contexts/AuthContext";
import AddOrEditAddressModal from "@/components/organisms/AddOrEditAddressModal";

interface DepartmentAddressTableProps {
  isAdmin: boolean;
  currentCompanyId?: number;
  departmentAddresses: CreateAddress[];
  addCompanyAddress: (address: CreateAddress) => void;
  removeCompanyAddress: (address: CreateAddress) => void;
}

const DepartmentAddressTable = (props: DepartmentAddressTableProps) => {
  const { user } = useAuth();
  const { isAdmin, currentCompanyId, departmentAddresses, addCompanyAddress, removeCompanyAddress } = props;
  const { t } = useTranslation();

  const { data: countries } = useGetCountries(user?.language?.id as number, {
    staleTime: 1000 * 60 * 5,
    enabled: !!user?.language?.id,
  });

  const [isAddEditAddressOpen, setIsAddEditAddressOpen] = useState<boolean>(false);

  const columns: ColumnDef<CreateAddress>[] = useMemo(
    () => [
      {
        header: t("common:address"),
        accessorKey: "address",
      },
      {
        header: t("common:postCode"),
        accessorKey: "postCode",
      },
      {
        header: t("common:place"),
        accessorKey: "place",
      },
      {
        header: t("common:country"),
        accessorKey: "countryId",
        cell: (info) => {
          const countryId = info.getValue() as number;
          const country = countries?.find((c) => c.id === countryId);
          return country?.name || "";
        },
      },
      {
        header: t("common:type"),
        accessorKey: "addressType",
        cell: (info) => {
          const rawAddressType = info.getValue() as AddressType;
          return t(`enumerables:addressTypes.${rawAddressType}`);
        },
      },
    ],
    [t]
  );

  const editActions = (addr: CreateAddress) => [
    {
      label: t("settings:delete"),
      action: async () => {
        removeCompanyAddress(addr);
      },
    },
  ];

  return (
    <React.Fragment>
      <SettingsFormContent>
        <Group justify="space-between">
          <Text size="md" strong={true} display="technical" tt="uppercase" data-testid={"department-address-heading"}>
            {t("common:address")}
          </Text>
          <CTA
            disabled={!isAdmin}
            id="add-konto"
            intent="secondary"
            data-testid={"add-department-address-button"}
            onClick={() => {
              setIsAddEditAddressOpen(true);
            }}
          >
            {t("settings:addAddress")}
          </CTA>
        </Group>
        <Space height="25px" />
        <SettingsTable data={departmentAddresses} columns={columns} isAdmin={isAdmin} editActions={editActions} />
      </SettingsFormContent>
      <AddOrEditAddressModal
        mode="create"
        isAddEditAddressOpen={isAddEditAddressOpen}
        setIsAddEditAddressOpen={setIsAddEditAddressOpen}
        companyId={currentCompanyId}
        callback={(addr) => {
          addCompanyAddress({
            address: addr.address,
            address2: addr.address2,
            address3: addr.address3,
            postCode: addr.postCode,
            place: addr.place,
            countryId: Number(addr.countryId),
            addressType: addr.addressType as AddressType,
            regionId: addr.regionId,
          });
        }}
      />
    </React.Fragment>
  );
};

export default DepartmentAddressTable;
